// components/use-case-details/use-case-workflow/use-case-workflow-vector-db-component.tsx
// components/use-case-details/use-case-workflow/use-case-workflow-vector-db-component.tsx
"use client";

import { motion } from 'framer-motion';
import { Code, Database, Search, SlidersHorizontal } from 'lucide-react';
import React from 'react';

import { TechTerm } from '@/components/common/tech-term-system';

interface VectorDBProps {
  title: string;
  description: string;
  databaseType: string;
  embeddingModel: string;
  dimensions: number;
  metricType: string;
  exampleQuery: string;
  exampleResult: string;
  industryContext: string;
  borderColor?: string;
  implementationSteps?: { step: string; description: string; detail?: string }[];
  implementationTips?: string[]; // New prop for tips!
}

const VectorDBComponent: React.FC<VectorDBProps> = ({
  title,
  description,
  databaseType,
  embeddingModel,
  dimensions,
  metricType,
  exampleQuery,
  exampleResult,
  industryContext,
  borderColor = "bg-blue-600",
  implementationSteps,
  implementationTips, // Receive tips as a prop
}) => {
  return (
    <motion.div
      className="relative bg-gray-800/50 rounded-lg shadow-lg border-b border-r border-l border-gray-700 mb-6 overflow-hidden flex flex-col"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0, transition: { duration: 0.4 } }}
      whileHover={{ scale: 1.01, transition: { duration: 0.2 } }}
    >
      {/* Top border and other sections (as corrected before) */}
      <div className={`absolute top-0 left-0 right-0 h-2 ${borderColor}`} />
      <div className="pt-4 flex flex-col px-3 pb-3 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center mb-3 md:mb-4">
          <div className="w-10 h-10 rounded-full bg-blue-700 flex items-center justify-center mb-2 sm:mb-0 sm:mr-3">
            <Database className="text-white" size={20} />
          </div>
          <div>
            <h4 className="text-xl font-bold text-white font-heading">{title}</h4>
            <p className="text-gray-300 text-sm font-body">{description}</p>
          </div>
        </div>

        <div className="mb-4 md:mb-6 bg-gray-900 p-3 md:p-4 rounded-lg">
          <h5 className="text-white font-bold mb-3 flex items-center font-heading">
            <SlidersHorizontal className="text-blue-400 mr-2" size={18} />
            Configuration
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            <div>
              <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">Database Type</span>
              <p className="text-white mb-2 md:mb-3 font-body">{databaseType}</p>
              <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                <TechTerm term="Embedding Model">Embedding Model</TechTerm>
              </span>
              <p className="text-white font-body">{embeddingModel}</p>
            </div>
            <div>
              <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                <TechTerm term="Vector Dimensions">Vector Dimensions</TechTerm>
              </span>
              <p className="text-white mb-2 md:mb-3 font-body">{dimensions}</p>
              <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                <TechTerm term="Distance Metric">Distance Metric</TechTerm>
              </span>
              <p className="text-white font-body">{metricType}</p>
            </div>
          </div>
        </div>

        {implementationSteps && (
          <div className="mb-6">
            <h5 className="text-white font-bold mb-4">Implementation Steps</h5>
            <ol className="space-y-3">
              {implementationSteps.map((step, index) => (
                <li key={index} className="flex items-start">
                  <div className="w-6 h-6 rounded-full bg-blue-700 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                    <span className="text-white text-xs">{index + 1}</span>
                  </div>
                  <div>
                    <p className="text-gray-300">{step.description}</p>
                    {step.detail && (
                      <p className="text-gray-500 text-sm italic">{step.detail}</p>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        <div className="bg-gray-900 p-4 rounded-lg mb-6">
          <h5 className="text-white font-bold mb-3 flex items-center">
            <Search className="text-green-400 mr-2" size={18} />
            <TechTerm term="Semantic Search">Example Query</TechTerm>
          </h5>
          <div className="mb-4">
            <div className="bg-gray-950 p-3 rounded mb-2 border border-gray-700">
              <p className="text-gray-300 font-mono text-sm">User Query: &quot;{exampleQuery}&quot;</p>
            </div>
            <div className="flex items-center mb-2">
              <Code className="text-purple-400 mr-2 flex-shrink-0" size={16} />
              <span className="text-gray-400 text-sm">Converts to vector using {embeddingModel}</span>
            </div>
            <div className="flex items-center">
              <Database className="text-blue-400 mr-2 flex-shrink-0" size={16} />
              <span className="text-gray-400 text-sm">Searches {databaseType} using {metricType} similarity</span>
            </div>
          </div>
          <h5 className="text-white font-bold mb-2">Result:</h5>
          <div className="bg-gray-950 p-3 rounded border border-gray-700">
            <p className="text-green-400 font-mono text-sm break-words">{exampleResult}</p>
          </div>
        </div>

        {/* Implementation Tips (NOW DYNAMIC) */}
        {implementationTips && (
          <div className="bg-gray-900 p-4 rounded-lg">
            <h5 className="text-white font-bold mb-3">Implementation Tips</h5>
            <ul className="space-y-2">
              {implementationTips.map((tip, index) => (
                <li key={index} className="text-gray-300 text-sm flex items-start">
                  <span className="text-yellow-400 mr-2 flex-shrink-0">•</span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default VectorDBComponent;


// // components/use-case-details/use-case-workflow/use-case-workflow-vector-db-component.tsx
// "use client";

// import React from 'react';
// import { motion } from 'framer-motion';
// import { Database, Code, Search, SlidersHorizontal } from 'lucide-react';
// import { TechTerm } from '@/components/common/tech-term-system';

// interface VectorDBProps {
//   title: string;
//   description: string;
//   databaseType: string;
//   embeddingModel: string;
//   dimensions: number;
//   metricType: string;
//   exampleQuery: string;
//   exampleResult: string;
//   industryContext: string;
//   borderColor?: string;
// }

// const VectorDBComponent: React.FC<VectorDBProps> = ({
//   title,
//   description,
//   databaseType,
//   embeddingModel,
//   dimensions,
//   metricType,
//   exampleQuery,
//   exampleResult,
//   industryContext,
//   borderColor = "bg-blue-600",
// }) => {
//   return (
//     <motion.div
//       className={`relative bg-gray-800/50 rounded-lg shadow-lg border-b border-r border-l border-gray-700 mb-6 overflow-hidden flex flex-col`}
//       initial={{ opacity: 0, y: 20 }}
//       animate={{ opacity: 1, y: 0, transition: { duration: 0.4 } }}
//       whileHover={{ scale: 1.01, transition: { duration: 0.2 } }}
//     >
//       {/* Top border as a separate element */}
//       <div className={`absolute top-0 left-0 right-0 h-2 ${borderColor}`}></div>
      
//       {/* Card content with better spacing - reduced padding on mobile */}
//       <div className="pt-4 flex flex-col px-3 pb-3 md:p-6">
//         <div className="flex flex-col sm:flex-row sm:items-center mb-3 md:mb-4">
//           <div className="w-10 h-10 rounded-full bg-blue-700 flex items-center justify-center mb-2 sm:mb-0 sm:mr-3">
//             <Database className="text-white" size={20} />
//           </div>
//           <div>
//             <h4 className="text-xl font-bold text-white font-heading">{title}</h4>
//             <p className="text-gray-300 text-sm font-body">{description}</p>
//           </div>
//         </div>

//         {/* Configuration Section */}
//         <div className="mb-4 md:mb-6 bg-gray-900 p-3 md:p-4 rounded-lg">
//           <h5 className="text-white font-bold mb-3 flex items-center font-heading">
//             <SlidersHorizontal className="text-blue-400 mr-2" size={18} />
//             Configuration
//           </h5>
//           <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
//             <div>
//               <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">Database Type</span>
//               <p className="text-white mb-2 md:mb-3 font-body">{databaseType}</p>
              
//               <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">
//                 <TechTerm term="Embedding Model">
//                   Embedding Model
//                 </TechTerm>
//               </span>
//               <p className="text-white font-body">{embeddingModel}</p>
//             </div>
//             <div>
//               <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">
//                 <TechTerm term="Vector Dimensions">
//                   Vector Dimensions
//                 </TechTerm>
//               </span>
//               <p className="text-white mb-2 md:mb-3 font-body">{dimensions}</p>
              
//               <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">
//                 <TechTerm term="Distance Metric">
//                   Distance Metric
//                 </TechTerm>
//               </span>
//               <p className="text-white font-body">{metricType}</p>
//             </div>
//           </div>
//         </div>
        
//         {/* Implementation Steps */}
//         <div className="mb-6">
//           <h5 className="text-white font-bold mb-4">Implementation Steps</h5>
//           <ol className="space-y-3">
//             <li className="flex items-start">
//               <div className="w-6 h-6 rounded-full bg-blue-700 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
//                 <span className="text-white text-xs">1</span>
//               </div>
//               <div>
//                 <p className="text-gray-300">Collect and clean {industryContext} data from source systems</p>
//                 <p className="text-gray-500 text-sm italic">Standardize formats, remove duplicates, handle missing values</p>
//               </div>
//             </li>
//             <li className="flex items-start">
//               <div className="w-6 h-6 rounded-full bg-blue-700 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
//                 <span className="text-white text-xs">2</span>
//               </div>
//               <div>
//                 <p className="text-gray-300">Generate vector embeddings using {embeddingModel}</p>
//                 <p className="text-gray-500 text-sm italic">Convert text data into {dimensions}-dimensional vectors</p>
//               </div>
//             </li>
//             <li className="flex items-start">
//               <div className="w-6 h-6 rounded-full bg-blue-700 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
//                 <span className="text-white text-xs">3</span>
//               </div>
//               <div>
//                 <p className="text-gray-300">Store embeddings in {databaseType} with metadata</p>
//                 <p className="text-gray-500 text-sm italic">Index using {metricType} similarity for optimal retrieval</p>
//               </div>
//             </li>
//             <li className="flex items-start">
//               <div className="w-6 h-6 rounded-full bg-blue-700 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
//                 <span className="text-white text-xs">4</span>
//               </div>
//               <div>
//                 <p className="text-gray-300">Query the database using the same embedding model</p>
//                 <p className="text-gray-500 text-sm italic">Convert search queries to vectors and find similar data</p>
//               </div>
//             </li>
//           </ol>
//         </div>

//         {/* Example Query Section */}
//         <div className="bg-gray-900 p-4 rounded-lg mb-6">
//           <h5 className="text-white font-bold mb-3 flex items-center">
//             <Search className="text-green-400 mr-2" size={18} />
//             <TechTerm term="Semantic Search">
//               Example Query
//             </TechTerm>
//           </h5>
//           <div className="mb-4">
//             <div className="bg-gray-950 p-3 rounded mb-2 border border-gray-700">
//               <p className="text-gray-300 font-mono text-sm">User Query: &quot;{exampleQuery}&quot;</p>
//             </div>
//             <div className="flex items-center mb-2">
//               <Code className="text-purple-400 mr-2 flex-shrink-0" size={16} />
//               <span className="text-gray-400 text-sm">Converts to vector using {embeddingModel}</span>
//             </div>
//             <div className="flex items-center">
//               <Database className="text-blue-400 mr-2 flex-shrink-0" size={16} />
//               <span className="text-gray-400 text-sm">Searches {databaseType} using {metricType} similarity</span>
//             </div>
//           </div>

//           <h5 className="text-white font-bold mb-2">Result:</h5>
//           <div className="bg-gray-950 p-3 rounded border border-gray-700">
//             <p className="text-green-400 font-mono text-sm break-words">{exampleResult}</p>
//           </div>
//         </div>

//         {/* Implementation Tips */}
//         <div className="bg-gray-900 p-4 rounded-lg">
//           <h5 className="text-white font-bold mb-3">Implementation Tips</h5>
//           <ul className="space-y-2">
//             <li className="text-gray-300 text-sm flex items-start">
//               <span className="text-yellow-400 mr-2 flex-shrink-0">•</span>
//               <span>Chunk your documents into smaller pieces (paragraphs) for more precise retrieval</span>
//             </li>
//             <li className="text-gray-300 text-sm flex items-start">
//               <span className="text-yellow-400 mr-2 flex-shrink-0">•</span>
//               <span>Include relevant metadata with each vector for filtering (date, category, author)</span>
//             </li>
//             <li className="text-gray-300 text-sm flex items-start">
//               <span className="text-yellow-400 mr-2 flex-shrink-0">•</span>
//               <span>Use higher dimensions ({dimensions}+) for more accurate representation of complex data</span>
//             </li>
//             <li className="text-gray-300 text-sm flex items-start">
//               <span className="text-yellow-400 mr-2 flex-shrink-0">•</span>
//               <span>Start with {metricType} similarity, but experiment with other metrics if results aren't ideal</span>
//             </li>
//           </ul>
//         </div>
//       </div>
//     </motion.div>
//   );
// };

// export default VectorDBComponent;
